/** Pinch-zoom and double-tap on a gallery slide. No-op if the slide or image is missing. */

interface StartPos {
  x: number | null
  y: number | null
  distance: number
}

interface ZoomState {
  lastScale: number
  lastX: number
  lastY: number
  imageElementScale: number
  deltaX: number
  deltaY: number
  start: StartPos
  lastTap: number
  moved: boolean
}

interface PinchTouchEvent extends TouchEvent {
  scale?: number
}

const states = new WeakMap<HTMLImageElement, ZoomState>()

const createState = (): ZoomState => ({
  lastScale: 1,
  lastX: 0,
  lastY: 0,
  imageElementScale: 1,
  deltaX: 0,
  deltaY: 0,
  start: {
    x: 0,
    y: 0,
    distance: 0
  },
  lastTap: 0,
  moved: false
})

const resetState = (state: ZoomState) => {
  state.lastScale = 1
  state.lastX = 0
  state.lastY = 0
  state.imageElementScale = 1
  state.deltaX = 0
  state.deltaY = 0
  state.start.x = null
  state.start.y = null
  state.start.distance = 0
  state.lastTap = 0
  state.moved = false
}

const applyTransform = (imageElement: HTMLImageElement, x: number, y: number, scale: number) => {
  const transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`
  imageElement.style.transform = transform
  imageElement.style.webkitTransform = transform
  imageElement.style.zIndex = scale > 1 ? '9999' : ''
}

export function pinchZoom (el: Element | null | undefined) {
  if (!el) {
    return
  }

  const imageElement = el.querySelector('img')
  if (!imageElement) {
    return
  }

  let state = states.get(imageElement)
  if (state) {
    // Slide changes clear the inline transform; keep the listeners and reset the gesture.
    resetState(state)
    applyTransform(imageElement, 0, 0, 1)
    return
  }

  state = createState()
  states.set(imageElement, state)
  const zoom = state
  let tracking = false

  const getPosition = (event: TouchEvent, attribute: 'pageX' | 'pageY') => {
    let pos = event.touches[0][attribute]
    if (event.touches[1]) {
      pos += event.touches[1][attribute]
    }
    return pos
  }

  const distance = (event: TouchEvent) => {
    return Math.hypot(
      event.touches[0].pageX - event.touches[1].pageX,
      event.touches[0].pageY - event.touches[1].pageY
    )
  }

  const touchmoveHandler = (event: TouchEvent) => {
    if (!event.touches || event.touches.length === 0 || zoom.start.x == null || zoom.start.y == null) {
      return
    }

    const moveX = getPosition(event, 'pageX') / 2
    const moveY = getPosition(event, 'pageY') / 2
    if (Math.hypot(moveX - zoom.start.x, moveY - zoom.start.y) > 8) {
      zoom.moved = true
    }

    // Keep the carousel from swiping the slide while the image is being pinched or panned.
    event.stopPropagation()
    if (event.cancelable) {
      event.preventDefault()
    }

    if (event.touches.length === 2) {
      const pinchEvent = event as PinchTouchEvent
      let scale = pinchEvent.scale
      if (!scale) {
        const deltaDistance = distance(event)
        scale = zoom.start.distance ? deltaDistance / zoom.start.distance : 1
      }
      scale *= zoom.lastScale
      zoom.imageElementScale = Math.min(Math.max(1, scale), 4)
    }

    zoom.deltaX = ((getPosition(event, 'pageX') / 2) - zoom.start.x) * 2 + zoom.lastX
    zoom.deltaY = ((getPosition(event, 'pageY') / 2) - zoom.start.y) * 2 + zoom.lastY

    const limitX = imageElement.offsetWidth * (zoom.imageElementScale - 1) / 2
    zoom.deltaX = zoom.deltaX > 0 ? Math.min(zoom.deltaX, limitX) : Math.max(zoom.deltaX, limitX * -1)
    const limitY = imageElement.offsetHeight * (zoom.imageElementScale - 1) / 2
    zoom.deltaY = zoom.deltaY > 0 ? Math.min(zoom.deltaY, limitY) : Math.max(zoom.deltaY, limitY * -1)

    applyTransform(imageElement, zoom.deltaX, zoom.deltaY, zoom.imageElementScale)
  }

  const finalizeTransform = () => {
    if (tracking) {
      imageElement.removeEventListener('touchmove', touchmoveHandler)
      imageElement.removeEventListener('touchend', finalizeTransform)
      tracking = false
    }

    zoom.lastX = zoom.deltaX
    zoom.lastY = zoom.deltaY
    zoom.lastScale = zoom.imageElementScale
    zoom.start.x = null
    zoom.start.y = null
  }

  const doubleTapHandler = () => {
    const newScale = zoom.imageElementScale === 1 ? 3 : 1
    zoom.imageElementScale = newScale
    zoom.lastScale = newScale
    zoom.lastX = 0
    zoom.lastY = 0
    zoom.deltaX = 0
    zoom.deltaY = 0
    applyTransform(imageElement, 0, 0, newScale)
  }

  const onTouchEnd = (event: TouchEvent) => {
    if (event.touches.length !== 0 || zoom.moved) {
      return
    }

    const currentTime = new Date().getTime()
    const tapLength = currentTime - zoom.lastTap
    if (tapLength > 50 && tapLength < 500) {
      doubleTapHandler()
      zoom.lastTap = 0
      return
    }
    zoom.lastTap = currentTime
  }

  const touchStartHandler = (event: TouchEvent) => {
    zoom.moved = false

    // One finger on an unzoomed image stays with the carousel so slides can still swipe.
    if (event.touches.length === 1 && zoom.imageElementScale === 1) {
      return
    }
    event.stopPropagation()
    if (event.cancelable) {
      event.preventDefault()
    }

    zoom.start.x = getPosition(event, 'pageX') / 2
    zoom.start.y = getPosition(event, 'pageY') / 2

    if (event.touches.length === 2) {
      zoom.start.distance = distance(event)
    }

    if (!tracking) {
      imageElement.addEventListener('touchmove', touchmoveHandler, { passive: false })
      imageElement.addEventListener('touchend', finalizeTransform)
      tracking = true
    }
  }

  imageElement.addEventListener('touchstart', touchStartHandler, { passive: false })
  imageElement.addEventListener('touchend', onTouchEnd)
}
